import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useShowToast } from '../../../../redux/slices/toasts/hooks';
import { useTRPCClientAPI, useTRPCMutation } from '../../../../trpc/trpc';
import type { ReplyThread } from '../outreachTypes';
import { inboxMessages as messages } from '../inbox/inboxMessages';
import { useInvalidateOutreach } from './useInvalidateOutreach';

/**
 * The actions of the thread side panel (design outreach-v2 m1): "She added it"
 * (`placements.addManual`) and "Never pitch again" (`blocks.add` of the person), each with a toast.
 */
export function useThreadSideActions(accountId: number, thread: ReplyThread) {
  const intl = useIntl();
  const trpcClient = useTRPCClientAPI();
  const invalidate = useInvalidateOutreach();
  const { showSuccess, showError } = useShowToast();
  const failed = useCallback(
    (error: unknown) => {
      console.error(error);
      showError(intl.formatMessage(messages.actionFailed));
    },
    [intl, showError]
  );

  const addManual = useTRPCMutation({
    mutationFn: (targetUuid: string) =>
      trpcClient.outreach.placements.addManual.mutate({ accountId, campaignUuid: thread.campaignUuid, targetUuid }),
    onSuccess: () => {
      invalidate(['inbox', 'campaigns', 'home']);
      showSuccess(intl.formatMessage(messages.addedItDone, { song: thread.songTitle }));
    },
    onError: failed,
  });
  const block = useTRPCMutation({
    mutationFn: () =>
      trpcClient.outreach.blocks.add.mutate({
        accountId,
        kind: 'contact',
        workspaceContactUuid: thread.workspaceContactUuid,
      }),
    onSuccess: () => {
      invalidate(['campaigns']);
      showSuccess(intl.formatMessage(messages.neverPitchDone, { name: thread.contactName }));
    },
    onError: failed,
  });

  return {
    markAdded: (targetUuid: string) => addManual.mutate(targetUuid),
    isMarkingAdded: addManual.isPending,
    neverPitch: () => block.mutate(),
    isBlocking: block.isPending,
  };
}
